'use client';

import { createRoute } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import { WaitlistCTA } from '@/components/marketing/WaitlistCTA';
import { Skeleton } from '@/components/ui';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  head: () => seoHead('/pricing'),
  component: Pricing,
});

// Canonical display price. Source of truth is Stripe (GET /api/v1/billing/pricing);
// keep this in sync with the seat price configured there.
const SEAT_PRICE_USD = 30;
const TRIAL_DAYS = 7;

function PricingContent() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  // Pricing is public content and must render independently of authentication.
  const showLoading = !mounted;
  const isDark = mounted && resolvedTheme === 'dark';

  // The stop-hook auto-fix loop leads the list as the differentiator.
  const included: string[] = [
    'Claude Code, Codex, Kimi Code, and Antigravity CLI stop hook integration and the auto-fix loop',
    'GitHub App pull request reviews: check runs and inline annotations',
    'On-demand reviews from your terminal, for a diff or the full repository',
    '75+ checks spanning security, performance, concurrency, and more',
    'Go, Python, TypeScript, and Rust: every check, every language',
    'Configurable thresholds and recorded, reviewable suppressions',
  ];

  if (showLoading) {
    return (
      <MarketingLayout isLoading={true} isSignedIn={false}>
        <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
          <div className="mx-auto max-w-[700px]">
            <Skeleton className="mx-auto mt-2 mb-6 h-12 w-[72%]" />
            <Skeleton className="mx-auto mb-12 h-6 w-[70%]" />
            <Skeleton className="mx-auto h-[38rem] w-full rounded-2xl" />
            <Skeleton className="mx-auto mt-8 h-4 w-[90%]" />
            <Skeleton className="mx-auto mt-2 h-4 w-[65%]" />
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout isLoading={false} isSignedIn={false}>
      <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
        <div className="mx-auto max-w-[700px] text-center">
          <h1
            className="text-gradient-hero mb-4 text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: theme.fonts.display, lineHeight: 1.2 }}
          >
            One price. Every check included.
          </h1>
          <p
            className="text-foreground/70 mb-12 text-lg md:text-xl"
            style={{ fontFamily: theme.fonts.body }}
          >
            Per seat, per month. No usage fees, no tiers to decode.
          </p>

          <div
            className="rounded-2xl p-8 md:p-12"
            style={{
              background: theme.box.gradient.bg,
              border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
            }}
          >
            <div className="mb-2 flex items-baseline justify-center gap-2">
              <span
                className="text-gradient-hero text-5xl font-bold md:text-6xl"
                // marginInline:0 overrides the `.text-center .text-gradient-hero { margin-inline: auto }`
                // rule (globals.css); on a flex item that auto-margin eats the free space and pushes
                // the "/ seat / month" unit to the far right.
                style={{ fontFamily: theme.fonts.display, marginInline: 0 }}
              >
                ${SEAT_PRICE_USD}
              </span>
              <span
                className="text-foreground/70 text-lg"
                style={{ fontFamily: theme.fonts.body }}
              >
                / seat / month
              </span>
            </div>
            <p
              className="text-foreground/70 mb-8 text-base"
              style={{ fontFamily: theme.fonts.body }}
            >
              {TRIAL_DAYS}-day free trial. Cancel anytime.
            </p>

            <ul
              className="mx-auto mb-8 max-w-[520px] space-y-3 text-left"
              style={{ fontFamily: theme.fonts.body }}
            >
              {included.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="mt-1"
                    style={{
                      color:
                        theme.colors.accent.primary[isDark ? 'dark' : 'light'],
                    }}
                  >
                    ✓
                  </span>
                  <span className="text-foreground/80">{item}</span>
                </li>
              ))}
            </ul>

            <WaitlistCTA label="Install the GitHub App" source="pricing" />

            <p
              className="text-foreground/70 mt-6 text-sm"
              style={{ fontFamily: theme.fonts.body }}
            >
              Early adopters get direct founder access and a say in what gets
              built.
            </p>
          </div>

          <p
            className="text-foreground/60 mt-8 text-sm"
            style={{ fontFamily: theme.fonts.body }}
          >
            A seat is one developer. Add or remove seats as your team changes,
            and billing adjusts automatically.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Pricing() {
  return (
    <Suspense
      fallback={
        <MarketingLayout isLoading={true} isSignedIn={false}>
          <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
            <div className="mx-auto max-w-[700px]">
              <Skeleton className="mx-auto mt-2 mb-6 h-12 w-[72%]" />
              <Skeleton className="mx-auto h-96 w-full rounded-2xl" />
            </div>
          </section>
        </MarketingLayout>
      }
    >
      <PricingContent />
    </Suspense>
  );
}
